import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import StationSwitcher from "@/components/StationSwitcher";
import NotificationBell from "@/components/NotificationBell";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import IframeEscapeBanner from "@/components/IframeEscapeBanner";
import NowPreviewingBar from "@/components/NowPreviewingBar";
import FlowWidget from "@/components/assistant/FlowWidget";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import CommandPalette from "@/components/CommandPalette";
import TourProvider from "@/components/tour/TourProvider";
import { Toaster, toast } from "sonner";
import { Menu } from "lucide-react";
import { api } from "@/lib/api";

export default function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Any navigation closes the mobile drawer (nav links, command palette, programmatic)
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Founding Testers — complete a claim started while logged out
  useEffect(() => {
    if (localStorage.getItem("founding_intent") !== "1") return;
    localStorage.removeItem("founding_intent");
    api.post("/founding/claim")
      .then(({ data }) => toast.success(data.already
        ? `You're already Founding Tester #${data.cohort_number}`
        : `Seat claimed — you're Founding Tester #${data.cohort_number}! Your 50% discount auto-applies at checkout.`))
      .catch((e) => toast.error(e.response?.data?.detail || "Founding seat could not be claimed."));
  }, []);

  return (
    <div className="min-h-screen bg-bg text-ink-primary">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)}
        mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)} data-testid="mobile-nav-backdrop" />
      )}
      <main className={`transition-all duration-300 ml-0 ${collapsed ? "md:ml-[72px]" : "md:ml-[248px]"}`}>
        <ImpersonationBanner />
        <IframeEscapeBanner />
        <div className="sticky top-0 z-20 flex h-14 items-center gap-2 sm:gap-3 border-b border-line bg-bg/70 backdrop-blur-xl px-3 sm:px-6">
          <button onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn"
            className="md:hidden shrink-0 rounded-md p-2 -ml-1 text-ink-secondary hover:text-ink-primary hover:bg-bg-elevated transition">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex flex-1 min-w-0 items-center justify-end gap-2 sm:gap-3">
            <div className="hidden md:contents"><CommandPalette /></div>
            <LanguageSwitcher />
            <NotificationBell />
            <StationSwitcher />
          </div>
        </div>
        <Outlet />
        {/* Mobile-only spacer so the floating assistant bot never covers page controls */}
        <div className="h-24 md:hidden" />
      </main>
      <TourProvider />
      <NowPreviewingBar />
      <FlowWidget />
      <Toaster
        theme="dark"
        position={typeof window !== "undefined" && window.innerWidth < 768 ? "top-center" : "bottom-right"}
        toastOptions={{
          duration: 3000,
          style: {
            background: "#0C0C10",
            border: "1px solid #272730",
            color: "#FAFAFA",
          },
        }}
      />
    </div>
  );
}
